var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { StyledTooltipContainer } from "./tooltip.styles";
import { TooltipPromptContainer } from "./tooltip-prompt-container.styles";
export function Tooltip(_a) {
    var { initialtooltipPosition = "right", promptText = "", children = _jsx("span", {}), onHoveredActive = true, onClickedActive = true, active = false, offset = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    } } = _a, restProps = __rest(_a, ["initialtooltipPosition", "promptText", "children", "onHoveredActive", "onClickedActive", "active", "offset"]);
    const clickedElement = useRef(null);
    const promptElement = useRef(null);
    const [isShowTooltip, setIsShowTooltip] = useState(active);
    const [tooltipPosition, setTooltipPosition] = useState(initialtooltipPosition);
    const [isShowTooltipByClick, setIsShowTooltipByClick] = useState(false);
    useEffect(() => {
        setIsShowTooltip(active);
    }, [active]);
    const toggleTooltip = () => {
        if (!active && onClickedActive) {
            setIsShowTooltipByClick(!isShowTooltipByClick);
        }
    };
    const toggleTooltipTouch = (ev) => {
        if (ev.touches.length >= 1) {
            setIsShowTooltipByClick(false);
        }
    };
    function getCoords(elem) {
        const box = elem.getBoundingClientRect();
        return {
            top: box.top,
            left: box.left,
            bottom: box.top + box.height,
            promptHeight: box.height,
            promptWidth: box.width,
            right: box.left + box.width,
            spaceRight: box.left + box.width * 2,
            spaceLeft: box.left - box.width,
            centerRight: box.right + box.width / 2,
            centerLeft: box.left + box.width / 2, // box.left when tooltip flips to top or Bottom
        };
    }
    const onDocumentClick = (ev) => {
        if (!active &&
            promptElement.current &&
            promptElement.current !== ev.target) {
            setIsShowTooltipByClick(false);
        }
    };
    function flipTooltip() {
        const windowInnerWidth = document.documentElement.clientWidth;
        if (isShowTooltip || isShowTooltipByClick) {
            if (promptElement.current) {
                const { top, left, bottom, right, spaceLeft, spaceRight, promptHeight, centerRight, } = getCoords(promptElement.current);
                if (tooltipPosition === "left" && left < 0) {
                    if (spaceRight < windowInnerWidth) {
                        setTooltipPosition("right");
                        return;
                    }
                    if (top + promptHeight < 0) {
                        setTooltipPosition("top");
                        return;
                    }
                    if (bottom + promptHeight < window.innerHeight) {
                        setTooltipPosition("bottom");
                        return;
                    }
                }
                if (tooltipPosition === "right" && right > windowInnerWidth) {
                    if (spaceLeft > 0) {
                        setTooltipPosition("left");
                        return;
                    }
                    if (top + promptHeight < 0) {
                        setTooltipPosition("top");
                        return;
                    }
                    if (bottom + promptHeight < window.innerHeight) {
                        setTooltipPosition("bottom");
                        return;
                    }
                }
                if (tooltipPosition === "bottom") {
                    if (left < 0 || right > windowInnerWidth) {
                        if (left < 0 && centerRight < windowInnerWidth) {
                            if (bottom > window.innerHeight) {
                                setTooltipPosition("topRight");
                                return;
                            }
                            setTooltipPosition("bottomRight");
                            return;
                        }
                        if (left > 0 && right > windowInnerWidth) {
                            if (bottom > window.innerHeight) {
                                setTooltipPosition("topLeft");
                                return;
                            }
                            setTooltipPosition("bottomLeft");
                            return;
                        }
                    }
                    else if (bottom > window.innerHeight) {
                        setTooltipPosition("top");
                        return;
                    }
                }
                if (tooltipPosition === "top") {
                    if (left < 0 || right > windowInnerWidth) {
                        if (left < 0 && centerRight < windowInnerWidth) {
                            if (top < 0) {
                                setTooltipPosition("bottomRight");
                                return;
                            }
                            setTooltipPosition("topRight");
                            return;
                        }
                        if (left > 0 && right > windowInnerWidth) {
                            if (top < 0) {
                                setTooltipPosition("bottomLeft");
                                return;
                            }
                            setTooltipPosition("topLeft");
                        }
                    }
                    else if (top < 0) {
                        setTooltipPosition("bottom");
                    }
                }
            }
        }
    }
    useEffect(() => {
        if (isShowTooltipByClick) {
            document.addEventListener("click", onDocumentClick);
            flipTooltip();
        }
        return () => {
            document.removeEventListener("click", onDocumentClick);
        };
    }, [isShowTooltipByClick]);
    useEffect(() => {
        if (isShowTooltip) {
            flipTooltip();
        }
    }, [isShowTooltip]);
    useEffect(() => {
        setTooltipPosition(initialtooltipPosition);
    }, [initialtooltipPosition]);
    // click this element to display tooltip
    const tooltipIcon = (_jsx("span", Object.assign({ role: "button", style: { outline: "none" }, ref: clickedElement, tabIndex: 0, onKeyUp: () => {
            if (!active) {
                setIsShowTooltipByClick(true);
            }
        }, onKeyDown: () => {
            if (!active) {
                setIsShowTooltipByClick(false);
            }
        }, onClick: toggleTooltip, onTouchStart: (ev) => toggleTooltipTouch(ev), onMouseEnter: () => {
            if (!active && onHoveredActive) {
                setIsShowTooltip(true);
            }
        }, onMouseLeave: () => {
            if (!active) {
                setIsShowTooltip(false);
            }
        } }, { children: children })));
    const tooltipPromptContainer = (_jsx(TooltipPromptContainer, Object.assign({ offset: offset, ref: promptElement, tooltipPosition: tooltipPosition, promptText: promptText }, { children: promptText })));
    return (_jsxs(StyledTooltipContainer, Object.assign({}, restProps, { children: [tooltipIcon, (isShowTooltip || isShowTooltipByClick) && tooltipPromptContainer] })));
}
